// Government-format Register PDF engine for KELTA Local Body registers.
// Implements Annexure-1A / 1 / 2A / 2B / 2 plus AMC, Connectivity and
// Service registers per Circular No. 74207/I.B.1/2013/LSGD (11.11.2014)
// and G.O.(Rt) No. 717/2026/LSGD (12.03.2026).
//
// Page numbering is computed with a two-pass render: pass 1 measures the
// document and records the real page where every asset/record appears,
// pass 2 prints the Index pages with those exact page numbers. Index
// references therefore always match Print Preview, Print and PDF Export.
import { jsPDF } from "jspdf";

const INK = [31, 41, 55];     // slate-800 — headings & values
const SUB = [107, 114, 128];  // gray-500 — small-caps labels
const RULE = [229, 231, 235]; // light card/table borders
const BLUE = [37, 99, 235];
const BLUE_BG = [239, 246, 255];
const ORANGE = [249, 115, 22];
const GREEN = [22, 163, 74];

const FOOTER_LINES = [
  "Circular No. 74207/I.B.1/2013/LSGD, Date: 11.11.2014",
  "G.O.(Rt) No. 717/2026/LSGD, Date: 12.03.2026",
];

const TITLES = {
  hw_index: { title: "HARDWARE REGISTER – INDEX PAGE", format: "IKM/TSIM/FMT/29-A" },
  hw: { title: "HARDWARE REGISTER", format: "IKM/TSIM/FMT/29" },
  cmp_index: { title: "COMPLAINT CALL LOGGING REGISTER – INDEX PAGE", format: "IKM/TSIM/FMT/19-A" },
  cmp_contacts: { title: "COMPLAINT CALL LOGGING REGISTER – CONTACTS PAGE", format: "IKM/TSIM/FMT/19-B" },
  cmp: { title: "COMPLAINT CALL LOGGING REGISTER", format: "IKM/TSIM/FMT/19" },
  amc: { title: "AMC REGISTER", format: "G.O.(Rt) 717/2026/LSGD" },
  connectivity: { title: "CONNECTIVITY REGISTER", format: "G.O.(Rt) 717/2026/LSGD" },
  service: { title: "SERVICE REGISTER", format: "G.O.(Rt) 717/2026/LSGD" },
};

export const PRINT_OPTIONS = [
  { id: "hardware", label: "Hardware / Stock Register", sections: ["hw_index", "hw"], hint: "Annexure-1A (Index) + Annexure-1" },
  { id: "complaints", label: "Maintenance / Complaint Register", sections: ["cmp_index", "cmp_contacts", "cmp"], hint: "Annexure-2A (Index) + 2B (Contacts) + 2" },
  { id: "amc", label: "AMC Register", sections: ["amc"], hint: "AMC Register" },
  { id: "connectivity", label: "Connectivity Register", sections: ["connectivity"], hint: "Connectivity Register" },
  { id: "service", label: "Service Register", sections: ["service"], hint: "Service Register" },
  { id: "all", label: "ALL Registers", sections: ["hw_index", "hw", "cmp_index", "cmp_contacts", "cmp", "amc", "connectivity", "service"], hint: "All 8 sections in official order" },
];

const ORIENTATION = { hw_index: "portrait", hw: "portrait", cmp_index: "portrait", cmp_contacts: "portrait", cmp: "portrait", amc: "portrait", connectivity: "portrait", service: "portrait" };

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(String(s).slice(0, 10) + "T00:00:00Z");
    if (isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(s); }
}

function assetType(it) {
  return it.subtype || it.type_of_instrument || it.category || "—";
}

/* ------------------------------------------------------------------ */
/* Engine                                                              */
/* ------------------------------------------------------------------ */
class RegisterEngine {
  constructor(localBody) {
    this.doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    this.localBody = localBody || "—";
    this.started = false;
    this.page = 0;
    this.pageMeta = {}; // page -> { title, format }
    this.y = 0;
    this.map = { hw: {}, cmp: {} };
  }

  dims() {
    const p = this.doc.internal.pageSize;
    return { w: p.getWidth(), h: p.getHeight() };
  }

  // KELTA brand strip: blue → orange → green across the top of every page
  brandStrip() {
    const { w } = this.dims();
    const M = this.margin;
    const total = w - 2 * M;
    const y = 6, h = 1.4;
    this.doc.setFillColor(...BLUE);
    this.doc.rect(M, y, total * 0.62, h, "F");
    this.doc.setFillColor(...ORANGE);
    this.doc.rect(M + total * 0.62, y, total * 0.23, h, "F");
    this.doc.setFillColor(...GREEN);
    this.doc.rect(M + total * 0.85, y, total * 0.15, h, "F");
  }

  startPage(sectionKey, continuation = false) {
    const o = ORIENTATION[sectionKey];
    if (!this.started) {
      this.started = true;
      if (o === "landscape") {
        // first page must be recreated in landscape
        this.doc.deletePage(1);
        this.doc.addPage("a4", "landscape");
      }
    } else {
      this.doc.addPage("a4", o);
    }
    this.page = this.doc.internal.getCurrentPageInfo().pageNumber;
    const meta = TITLES[sectionKey];
    this.pageMeta[this.page] = meta;
    const { w } = this.dims();
    const M = 12;
    this.brandStrip();
    // Eyebrow — blue small caps
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(7.5);
    this.doc.setTextColor(...BLUE);
    this.doc.text("LOCAL BODY · TECHNICAL ASSISTANT REGISTERS", M, 13.5);
    // Register Title — big bold ink
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(15);
    this.doc.setTextColor(...INK);
    this.doc.text(meta.title + (continuation ? " (contd.)" : ""), M, 21);
    // Local Body — gray subtitle
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(...SUB);
    this.doc.text(`Name of Local Body: ${this.localBody}`, M, 27.5);
    this.doc.setDrawColor(...RULE);
    this.doc.setLineWidth(0.3);
    this.doc.line(M, 30.5, w - M, 30.5);
    this.y = 36;
  }

  get bottom() {
    return this.dims().h - 20;
  }

  get margin() {
    return 12;
  }

  get usable() {
    return this.dims().w - 24;
  }

  ensure(h, sectionKey, repeatHeader) {
    if (this.y + h > this.bottom) {
      this.startPage(sectionKey, true);
      if (repeatHeader) repeatHeader();
    }
  }

  text(s, x, y, opts = {}) {
    this.doc.setFont("helvetica", opts.style || "normal");
    this.doc.setFontSize(opts.size || 8.5);
    this.doc.setTextColor(...(opts.color || INK));
    this.doc.text(String(s ?? "—"), x, y, { align: opts.align || "left" });
  }

  // Bordered, wrapping table. columns: [{label, w, key, align, size}]
  // onRow(i) called with the page number where row i starts.
  table(sectionKey, columns, rows, opts = {}) {
    const M = this.margin;
    const rowPad = 1.8;
    const fontSize = opts.fontSize || 7.5;
    const headSize = opts.headSize || 6.5;
    const lineH = fontSize * 0.42;
    // Dynamic header height — labels wrap fully within their column, never overlap
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(headSize);
    const headLines = columns.map(c => this.doc.splitTextToSize(c.label.toUpperCase(), c.w - 2));
    const headH = Math.max(8, Math.max(...headLines.map(l => Math.min(l.length, 5))) * 2.3 + 2.8);

    const drawHead = () => {
      let x = M;
      columns.forEach((c, ci) => {
        this.doc.setFillColor(241, 245, 249);
        this.doc.setDrawColor(...RULE);
        this.doc.setLineWidth(0.25);
        this.doc.rect(x, this.y, c.w, headH, "FD");
        this.doc.setFont("helvetica", "bold");
        this.doc.setFontSize(headSize);
        this.doc.setTextColor(...SUB);
        headLines[ci].slice(0, 5).forEach((ln, li) => {
          this.doc.text(ln, x + c.w / 2, this.y + 3 + li * 2.3, { align: "center" });
        });
        x += c.w;
      });
      this.y += headH;
    };

    drawHead();
    if (!rows.length) {
      const h = 8;
      this.ensure(h + 2, sectionKey, drawHead);
      this.doc.setDrawColor(...RULE);
      this.doc.rect(M, this.y, this.usable, h);
      this.text(opts.emptyText || "No entries recorded.", M + this.usable / 2, this.y + 5, { align: "center", size: 8, color: SUB });
      this.y += h;
      return;
    }
    rows.forEach((row, i) => {
      // measure height
      let maxLines = 1;
      const cellLines = columns.map(c => {
        const v = typeof c.key === "function" ? c.key(row, i) : row[c.key];
        const lines = this.doc.splitTextToSize(String(v ?? "—") || "—", c.w - 2.5);
        maxLines = Math.max(maxLines, lines.length);
        return lines;
      });
      const h = Math.max(opts.minRowH || 8, maxLines * lineH + rowPad * 2);
      this.ensure(h, sectionKey, drawHead);
      if (opts.onRow) opts.onRow(i, this.page);
      // subtle zebra striping for readability
      if (i % 2 === 1) {
        this.doc.setFillColor(249, 250, 251);
        this.doc.rect(M, this.y, this.usable, h, "F");
      }
      let x = M;
      columns.forEach((c, ci) => {
        this.doc.setDrawColor(...RULE);
        this.doc.setLineWidth(0.25);
        this.doc.rect(x, this.y, c.w, h);
        this.doc.setFont("helvetica", "normal");
        this.doc.setFontSize(opts.fontSize || 7.5);
        this.doc.setTextColor(...INK);
        const tx = c.align === "center" ? x + c.w / 2 : x + 1.5;
        cellLines[ci].forEach((ln, li) => {
          this.doc.text(ln, tx, this.y + rowPad + 2.2 + li * lineH, { align: c.align === "center" ? "center" : "left" });
        });
        x += c.w;
      });
      this.y += h;
    });
  }

  // Rounded field cards — old-design style: small-caps gray label above bold value
  fieldGrid(sectionKey, pairs, cols = 2) {
    const M = this.margin;
    const gap = 3;
    const colW = (this.usable - gap * (cols - 1)) / cols;
    let i = 0;
    while (i < pairs.length) {
      const rowPairs = pairs.slice(i, i + cols);
      // measure
      let maxLines = 1;
      const valueLines = rowPairs.map(([, v]) => {
        const lines = this.doc.splitTextToSize(String(v || "—"), colW - 6);
        maxLines = Math.max(maxLines, lines.length);
        return lines;
      });
      const h = Math.max(13.5, 6 + maxLines * 3.9 + 3);
      this.ensure(h + gap, sectionKey);
      rowPairs.forEach(([label], j) => {
        const x = M + j * (colW + gap);
        this.doc.setDrawColor(...RULE);
        this.doc.setLineWidth(0.3);
        this.doc.roundedRect(x, this.y, colW, h, 2, 2);
        this.doc.setFont("helvetica", "bold");
        this.doc.setFontSize(6.3);
        this.doc.setTextColor(...SUB);
        this.doc.text(label.toUpperCase(), x + 3, this.y + 4.6);
        this.doc.setFont("helvetica", "bold");
        this.doc.setFontSize(9);
        this.doc.setTextColor(...INK);
        valueLines[j].forEach((ln, li) => this.doc.text(ln, x + 3, this.y + 10 + li * 3.9));
      });
      this.y += h + gap;
      i += cols;
    }
  }

  drawFooters() {
    const total = this.page;
    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    for (let p = 1; p <= total; p++) {
      this.doc.setPage(p);
      const { w, h } = this.dims();
      const M = 12;
      this.doc.setDrawColor(...RULE);
      this.doc.setLineWidth(0.3);
      this.doc.line(M, h - 15, w - M, h - 15);
      // Old-design footer line, left
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(7.5);
      this.doc.setTextColor(...SUB);
      this.doc.text(`Generated · ${today} · Page ${p} of ${total}`, M, h - 10.5);
      // Official references — bottom right, both lines (required)
      this.doc.setFontSize(7);
      this.doc.setTextColor(...SUB);
      this.doc.text(FOOTER_LINES[0], w - M, h - 10.5, { align: "right" });
      this.doc.text(FOOTER_LINES[1], w - M, h - 6.5, { align: "right" });
    }
  }
}

/* ------------------------------------------------------------------ */
/* Cover & closing certificate pages                                   */
/* ------------------------------------------------------------------ */
const REGISTER_GROUPS = [
  { id: "hardware", sections: ["hw_index", "hw"], title: TITLES.hw.title, format: TITLES.hw.format },
  { id: "complaints", sections: ["cmp_index", "cmp_contacts", "cmp"], title: TITLES.cmp.title, format: TITLES.cmp.format },
  { id: "amc", sections: ["amc"], title: TITLES.amc.title, format: TITLES.amc.format },
  { id: "connectivity", sections: ["connectivity"], title: TITLES.connectivity.title, format: TITLES.connectivity.format },
  { id: "service", sections: ["service"], title: TITLES.service.title, format: TITLES.service.format },
];

function groupEntryCount(group, bundle) {
  if (group.id === "hardware") return (bundle.hardware || []).length;
  return (bundle[group.id] || []).length;
}

function renderCover(eng, group, bundle) {
  // Portrait cover page — old-design brand language
  if (eng.started) eng.doc.addPage("a4", "portrait");
  else eng.started = true;
  eng.page = eng.doc.internal.getCurrentPageInfo().pageNumber;
  eng.pageMeta[eng.page] = { title: group.title, format: group.format };
  const d = eng.doc;
  const W = 210, H = 297, M = 20;
  const cx = W / 2;
  eng.brandStrip();
  // Eyebrow
  d.setFont("helvetica", "bold"); d.setFontSize(8); d.setTextColor(...BLUE);
  d.text("LOCAL SELF GOVERNMENT DEPARTMENT", cx, 40, { align: "center" });
  // Register title — big bold ink
  d.setFontSize(20); d.setTextColor(...INK);
  const titleLines = d.splitTextToSize(group.title, W - 2 * M - 10);
  titleLines.forEach((ln, i) => d.text(ln, cx, 56 + i * 9, { align: "center" }));
  const afterTitle = 56 + titleLines.length * 9;
  // Orange accent bar under the title
  d.setFillColor(...ORANGE);
  d.roundedRect(cx - 22, afterTitle + 1, 44, 1.8, 0.9, 0.9, "F");
  // Local body
  d.setFont("helvetica", "normal"); d.setFontSize(12); d.setTextColor(...SUB);
  d.text(`Name of Local Body: ${eng.localBody}`, cx, afterTitle + 13, { align: "center" });
  // Seal box (uploaded seal image if available) — rounded, dashed
  const sealY = afterTitle + 24;
  d.setDrawColor(...RULE); d.setLineWidth(0.4);
  if (bundle.seal_image) {
    try {
      d.addImage(bundle.seal_image, cx - 24, sealY, 48, 42, undefined, "FAST");
    } catch {
      drawSealPlaceholder(d, cx, sealY);
    }
  } else {
    drawSealPlaceholder(d, cx, sealY);
  }
  // In-charge
  const icY = sealY + 56;
  d.setFont("helvetica", "bold"); d.setFontSize(7.5); d.setTextColor(...SUB);
  d.text("REGISTER IN-CHARGE", cx, icY, { align: "center" });
  d.setFontSize(11.5); d.setTextColor(...INK);
  d.text(bundle.member_name || "__________________________", cx, icY + 7, { align: "center" });
  d.setFont("helvetica", "normal"); d.setFontSize(9.5); d.setTextColor(...SUB);
  d.text("Technical Assistant", cx, icY + 13.5, { align: "center" });
  // Opening certificate — rounded card
  const ocY = icY + 26;
  d.setDrawColor(...RULE); d.setLineWidth(0.4);
  d.roundedRect(M, ocY, W - 2 * M, 54, 3, 3);
  d.setFillColor(...ORANGE);
  d.roundedRect(M + 6, ocY + 8.5, 10, 1.4, 0.7, 0.7, "F");
  d.setFont("helvetica", "bold"); d.setFontSize(9); d.setTextColor(...INK);
  d.text("OPENING CERTIFICATE", M + 6, ocY + 7);
  d.setFont("helvetica", "normal"); d.setFontSize(9.5); d.setTextColor(...INK);
  const pages = eng.map.groupPages?.[group.id];
  const cert = `Certified that this register has been opened on ____ / ____ / ________ and contains ${pages ? `${pages} ` : "____ "}serially numbered pages. All entries in this register are maintained as per Circular No. 74207/I.B.1/2013/LSGD dated 11.11.2014 and G.O.(Rt) No. 717/2026/LSGD dated 12.03.2026.`;
  d.splitTextToSize(cert, W - 2 * M - 12).forEach((ln, i) => d.text(ln, M + 6, ocY + 17 + i * 4.6));
  d.setFontSize(8.5); d.setTextColor(...SUB);
  d.text("Signature of Technical Assistant", M + 8, ocY + 46);
  d.text("Signature of Secretary / Approving Authority", W - M - 8, ocY + 46, { align: "right" });
  d.text("Date:", M + 8, ocY + 51.5);
  d.text("Date:", W - M - 30, ocY + 51.5);
  eng.y = H - 30;
}

function drawSealPlaceholder(d, cx, sealY) {
  d.setDrawColor(...RULE); d.setLineWidth(0.4);
  try { d.setLineDashPattern([2, 2], 0); } catch { /* older jspdf */ }
  d.roundedRect(cx - 32, sealY, 64, 42, 3, 3);
  try { d.setLineDashPattern([], 0); } catch { /* noop */ }
  d.setFont("helvetica", "normal"); d.setFontSize(9); d.setTextColor(...SUB);
  d.text("Seal of the Local Body", cx, sealY + 23, { align: "center" });
}

function renderClosing(eng, group, bundle) {
  eng.doc.addPage("a4", "portrait");
  eng.page = eng.doc.internal.getCurrentPageInfo().pageNumber;
  eng.pageMeta[eng.page] = { title: group.title, format: group.format };
  const d = eng.doc;
  const W = 210, M = 20, cx = W / 2;
  eng.brandStrip();
  d.setFont("helvetica", "bold"); d.setFontSize(7.5); d.setTextColor(...BLUE);
  d.text("LOCAL BODY · TECHNICAL ASSISTANT REGISTERS", M, 13.5);
  d.setFontSize(14); d.setTextColor(...INK);
  d.text(group.title, cx, 58, { align: "center" });
  d.setFillColor(...ORANGE);
  d.roundedRect(cx - 22, 61, 44, 1.8, 0.9, 0.9, "F");
  d.setFont("helvetica", "normal"); d.setFontSize(11); d.setTextColor(...SUB);
  d.text(`Name of Local Body: ${eng.localBody}`, cx, 70, { align: "center" });
  const y = 92;
  d.setDrawColor(...RULE); d.setLineWidth(0.4);
  d.roundedRect(M, y, W - 2 * M, 60, 3, 3);
  d.setFillColor(...ORANGE);
  d.roundedRect(M + 6, y + 8.5, 10, 1.4, 0.7, 0.7, "F");
  d.setFont("helvetica", "bold"); d.setFontSize(9); d.setTextColor(...INK);
  d.text("CLOSING CERTIFICATE", M + 6, y + 7);
  d.setFont("helvetica", "normal"); d.setFontSize(9.5);
  const count = groupEntryCount(group, bundle);
  const cert = `Certified that this register has been closed on ____ / ____ / ________. Total entries recorded in this register: ${count}. The entries have been verified and found correct.`;
  d.splitTextToSize(cert, W - 2 * M - 12).forEach((ln, i) => d.text(ln, M + 6, y + 17 + i * 4.6));
  d.setFontSize(8.5); d.setTextColor(...SUB);
  d.text("Signature of Technical Assistant", M + 8, y + 50);
  d.text("Signature of Secretary / Approving Authority", W - M - 8, y + 50, { align: "right" });
  d.text("Date:", M + 8, y + 56);
  d.text("Date:", W - M - 30, y + 56);
  eng.y = y + 60;
}

/* ------------------------------------------------------------------ */
/* Section renderers                                                   */
/* ------------------------------------------------------------------ */
function renderHwIndex(eng, hardware) {
  eng.startPage("hw_index");
  const U = eng.usable;
  eng.table("hw_index", [
    { label: "Sl. No.", w: 16, key: (r, i) => String(i + 1), align: "center" },
    { label: "Asset Type", w: U * 0.30, key: r => assetType(r) },
    { label: "Asset ID", w: U * 0.26, key: r => r.asset_id || "—" },
    { label: "Installation Date", w: U * 0.16, key: r => fmtDate(r.date_of_supply), align: "center" },
    { label: "Page No.", w: U - 16 - U * 0.72, key: r => String(eng.map.hw[r.id] ?? ""), align: "center" },
  ], hardware, { minRowH: 8 });
}

function renderHwRegister(eng, hardware, complaintsByHw, serviceByHw) {
  hardware.forEach((it, idx) => {
    eng.startPage("hw");
    eng.map.hw[it.id] = eng.page;
    // Eyebrow + Asset ID headline + status pill (old-design style)
    eng.text(`EQUIPMENT · SL. NO. ${it.sl_no || idx + 1}`, eng.margin, eng.y, { style: "bold", size: 7.5, color: BLUE });
    const st = it.status || "Active";
    const pillCol = st === "Active" ? GREEN : (st.startsWith("Disposed") ? [220, 38, 38] : [217, 119, 6]);
    eng.doc.setFont("helvetica", "bold");
    eng.doc.setFontSize(7.5);
    const pw = eng.doc.getTextWidth(st) + 8;
    const pillX = eng.margin + eng.usable - pw;
    eng.doc.setFillColor(pillCol[0], pillCol[1], pillCol[2]);
    eng.doc.setDrawColor(pillCol[0], pillCol[1], pillCol[2]);
    eng.doc.setTextColor(255, 255, 255);
    eng.doc.roundedRect(pillX, eng.y - 3.4, pw, 6.4, 3.2, 3.2, "FD");
    eng.doc.text(st, pillX + pw / 2, eng.y + 0.9, { align: "center" });
    eng.y += 4;
    eng.doc.setFont("helvetica", "bold");
    eng.doc.setFontSize(13);
    eng.doc.setTextColor(...INK);
    eng.doc.text(it.asset_id || "—", eng.margin, eng.y + 3);
    eng.y += 9;
    const complaints = complaintsByHw[it.id] || [];
    const services = serviceByHw[it.id] || [];
    const lastHistory = (it.status_history || []).slice(-1)[0];
    eng.fieldGrid("hw", [
      ["Asset ID", it.asset_id],
      ["Status", it.status || "Active"],
      ["Asset Type", assetType(it)],
      ["Hardware Category", it.category],
      ["Brand / Make", it.brand],
      ["Model", it.manufacturer_model],
      ["Serial Number", it.serial_no],
      ["Installation Date", fmtDate(it.date_of_supply)],
      ["Purchase Details", it.bill_no_date || it.supplied_by],
      ["Location", it.institution_name || it.local_body_name],
      ["Assigned User / Department", it.assigned_user],
      ["Present Condition", it.present_condition],
      ["Technical Specifications", it.specifications],
      ["Warranty", (it.initial_warranty_from || it.initial_warranty_to)
        ? `${fmtDate(it.initial_warranty_from)} to ${fmtDate(it.initial_warranty_to)}${it.warranty_period ? ` (${it.warranty_period} yr)` : ""}` : "—"],
      ["AMC Details", (it.amc_from || it.amc_to || it.service_provider)
        ? `${it.service_provider || ""} ${it.amc_from ? `| ${fmtDate(it.amc_from)} to ${fmtDate(it.amc_to)}` : ""}${it.amc_no_amount ? ` | ${it.amc_no_amount}` : ""}`.trim() : "—"],
      ["Vendor & Contact Details", [it.vendor_name, it.vendor_contact_person, it.vendor_phone, it.vendor_email, it.vendor_address].filter(Boolean).join(", ")],
      ["Complaint History", complaints.length ? `${complaints.length} complaint(s) logged — see Complaint Call Logging Register` : "No complaints recorded"],
      ["Service History", services.length ? `${services.length} service(s) recorded — see Service Register` : "No services recorded"],
      ["Disposal / E-Waste Status", (it.status || "").startsWith("Disposed") ? `${it.status}${lastHistory?.at ? ` on ${fmtDate(lastHistory.at)}` : ""}` : "—"],
      ["QR Code", it.asset_id ? `QR label printed for Asset ID ${it.asset_id}` : "—"],
    ]);
  });
  if (!hardware.length) {
    eng.startPage("hw");
    eng.table("hw", [{ label: "Hardware Register", w: eng.usable, key: () => "" }], [], {});
  }
}

function renderCmpIndex(eng, hardware, complaintsByHw) {
  eng.startPage("cmp_index");
  const rows = hardware.filter(it => (complaintsByHw[it.id] || []).length);
  const U = eng.usable;
  eng.table("cmp_index", [
    { label: "Sl. No.", w: 16, key: (r, i) => String(i + 1), align: "center" },
    { label: "Asset Type", w: U * 0.30, key: r => assetType(r) },
    { label: "Asset ID", w: U * 0.26, key: r => r.asset_id || "—" },
    { label: "Installation Date", w: U * 0.16, key: r => fmtDate(r.date_of_supply), align: "center" },
    { label: "Page No.", w: U - 16 - U * 0.72, key: r => String(eng.map.cmp[r.id] ?? ""), align: "center" },
  ], rows, { minRowH: 8 });
}

function renderCmpContacts(eng, hardware, amc) {
  eng.startPage("cmp_contacts");
  const hwRows = (hardware || [])
    .filter(it => it.service_provider || it.vendor_name)
    .map(it => ({
      provider: it.service_provider || it.vendor_name,
      asset_type: assetType(it),
      contact: it.vendor_phone || "—",
      page: String(eng.map.cmp[it.id] ?? "—"),
    }));
  const amcRows = (amc || [])
    .filter(a => a.amc_vendor)
    .map(a => ({
      provider: a.amc_vendor,
      asset_type: a.equipment || a.asset_type || "—",
      contact: a.contact_number || "—",
      page: String(eng.map.cmp[a.hardware_id] ?? "—"),
    }));
  const rows = [...hwRows, ...amcRows];
  const U = eng.usable;
  eng.table("cmp_contacts", [
    { label: "Sl. No.", w: 16, key: (r, i) => String(i + 1), align: "center" },
    { label: "Service Provider", w: U * 0.32, key: "provider" },
    { label: "Asset Type", w: U * 0.24, key: "asset_type" },
    { label: "Help Desk Contact No.", w: U * 0.20, key: "contact", align: "center" },
    { label: "Page No.", w: U - 16 - U * 0.76, key: "page", align: "center" },
  ], rows, { minRowH: 8 });
}

function renderCmpRegister(eng, hardware, complaintsByHw, allComplaints) {
  const rows = hardware.filter(it => (complaintsByHw[it.id] || []).length);
  const unlinked = (allComplaints || []).filter(c => !c.hardware_id);
  if (!rows.length && !unlinked.length) {
    eng.startPage("cmp");
    eng.table("cmp", [{ label: "Complaint Call Logging Register", w: eng.usable, key: () => "" }], [], {});
    return;
  }
  rows.forEach((it) => {
    eng.startPage("cmp");
    eng.map.cmp[it.id] = eng.page;
    // Top section: asset information strip
    eng.fieldGrid("cmp", [
      ["Asset ID", it.asset_id],
      ["Asset Type", assetType(it)],
      ["Make", it.brand],
      ["Model", it.manufacturer_model],
      ["Sl. No.", String(it.sl_no || "—")],
      ["Serial Number", it.serial_no],
    ], 3);
    eng.y += 3;
    const U = eng.usable;
    eng.table("cmp", [
      { label: "Sl.No", w: 9, key: (r, i) => String(i + 1), align: "center" },
      { label: "Reporting Date", w: 17, key: r => fmtDate(r.reporting_date), align: "center" },
      { label: "Comp. ID", w: 15, key: r => r.comp_id || "—", align: "center" },
      { label: "Complaint Remarks", w: 34, key: "complaint_remarks" },
      { label: "Service Date", w: 17, key: r => fmtDate(r.service_date), align: "center" },
      { label: "Solved Status Yes / No", w: 13, key: r => r.solved || "—", align: "center" },
      { label: "Service Remarks", w: 32, key: "service_remarks" },
      { label: "Name of Technician & Contact Number", w: 30, key: "technician_contact" },
      { label: "Tracking Number", w: U - 167, key: "tracking_number", align: "center" },
    ], complaintsByHw[it.id] || [], { fontSize: 7 });
  });
  // General complaints not linked to any hardware asset get their own page
  if (unlinked.length) {
    eng.startPage("cmp");
    eng.fieldGrid("cmp", [
      ["Asset ID", "—"], ["Asset Type", "General / Unlinked"], ["Make", "—"],
      ["Model", "—"], ["Sl. No.", "—"], ["Serial Number", "—"],
    ], 3);
    eng.y += 3;
    const U = eng.usable;
    eng.table("cmp", [
      { label: "Sl.No", w: 9, key: (r, i) => String(i + 1), align: "center" },
      { label: "Reporting Date", w: 17, key: r => fmtDate(r.reporting_date), align: "center" },
      { label: "Comp. ID", w: 15, key: r => r.comp_id || "—", align: "center" },
      { label: "Complaint Remarks", w: 34, key: "complaint_remarks" },
      { label: "Service Date", w: 17, key: r => fmtDate(r.service_date), align: "center" },
      { label: "Solved Status Yes / No", w: 13, key: r => r.solved || "—", align: "center" },
      { label: "Service Remarks", w: 32, key: "service_remarks" },
      { label: "Name of Technician & Contact Number", w: 30, key: "technician_contact" },
      { label: "Tracking Number", w: U - 167, key: "tracking_number", align: "center" },
    ], unlinked, { fontSize: 7 });
  }
}

function renderAmc(eng, amc) {
  eng.startPage("amc");
  const U = eng.usable;
  eng.table("amc", [
    { label: "Sl. No.", w: 8, key: (r, i) => String(i + 1), align: "center" },
    { label: "Asset ID", w: 19, key: r => r.asset_id || "—" },
    { label: "Asset Type / Equipment", w: 20, key: r => r.equipment || r.asset_type || "—" },
    { label: "AMC Vendor", w: 19, key: "amc_vendor" },
    { label: "Contact Person & Number", w: 21, key: r => [r.contact_person, r.contact_number].filter(Boolean).join(" · ") || "—" },
    { label: "AMC Start Date", w: 14, key: r => fmtDate(r.amc_start), align: "center" },
    { label: "AMC End Date", w: 14, key: r => fmtDate(r.amc_end), align: "center" },
    { label: "Contract Number", w: 13, key: "contract_number", align: "center" },
    { label: "AMC Amount", w: 11, key: "amc_amount", align: "center" },
    { label: "Service Coverage", w: 17, key: "service_coverage" },
    { label: "Status", w: 10, key: "status", align: "center" },
    { label: "Remarks", w: U - 166, key: "remarks" },
  ], amc, { fontSize: 7 });
}

function renderConnectivity(eng, rows) {
  eng.startPage("connectivity");
  const U = eng.usable;
  eng.table("connectivity", [
    { label: "Sl. No.", w: 8, key: (r, i) => String(i + 1), align: "center" },
    { label: "Connection Type", w: 19, key: "connection_type" },
    { label: "Service Provider", w: 19, key: "service_provider" },
    { label: "Connection ID / Account Number", w: 20, key: "connection_id", align: "center" },
    { label: "Location", w: 18, key: "location" },
    { label: "Bandwidth / Speed", w: 14, key: "bandwidth", align: "center" },
    { label: "Installation Date", w: 15, key: r => fmtDate(r.installation_date), align: "center" },
    { label: "Monthly Cost", w: 11, key: "monthly_cost", align: "center" },
    { label: "Contact Number", w: 16, key: "contact_number", align: "center" },
    { label: "Router / Modem Details", w: 20, key: "router_modem_details" },
    { label: "Status", w: 10, key: "status", align: "center" },
    { label: "Remarks", w: U - 170, key: "remarks" },
  ], rows, { fontSize: 7 });
}

function renderService(eng, rows) {
  eng.startPage("service");
  const U = eng.usable;
  eng.table("service", [
    { label: "Sl. No.", w: 8, key: (r, i) => String(i + 1), align: "center" },
    { label: "Equipment Type", w: 16, key: "equipment_type" },
    { label: "Asset ID", w: 16, key: r => r.asset_id || "—" },
    { label: "Make / Model / Serial", w: 24, key: r => `${[r.make || r.asset_make, r.model || r.asset_model].filter(Boolean).join(" ") || "—"}${(r.serial_number || r.asset_serial) ? ` · SN: ${r.serial_number || r.asset_serial}` : ""}` },
    { label: "Service Date", w: 14, key: r => fmtDate(r.service_date), align: "center" },
    { label: "Service Provider", w: 16, key: "service_provider" },
    { label: "Service Details", w: 22, key: "service_details" },
    { label: "Consumable Used", w: 14, key: "consumable_used" },
    { label: "Qty", w: 8, key: "quantity", align: "center" },
    { label: "Cost", w: 10, key: "cost", align: "center" },
    { label: "Technician", w: 14, key: "technician" },
    { label: "Next Service Date", w: 14, key: r => fmtDate(r.next_service_date), align: "center" },
    { label: "Remarks", w: U - 176, key: "remarks" },
  ], rows, { fontSize: 7 });
}

/* ------------------------------------------------------------------ */
/* Public API — two-pass build for accurate index page numbers         */
/* ------------------------------------------------------------------ */
function groupBy(arr, key) {
  const out = {};
  (arr || []).forEach(r => {
    const k = r[key] || "";
    if (!k) return;
    (out[k] = out[k] || []).push(r);
  });
  return out;
}

/* Complaints whose hardware asset was (soft-)deleted still get their own
   register page, built from the denormalised asset fields stored on the
   complaint entry, and stay referenced from the Annexure-2A index. */
function pseudoAssetsFromOrphans(complaints, hardware) {
  const hwIds = new Set((hardware || []).map(h => h.id));
  const groups = groupBy((complaints || []).filter(c => c.hardware_id && !hwIds.has(c.hardware_id)), "hardware_id");
  return Object.keys(groups).map(hid => ({
    id: hid,
    asset_id: groups[hid][0].asset_id || "—",
    subtype: groups[hid][0].asset_type,
    brand: groups[hid][0].asset_make,
    manufacturer_model: groups[hid][0].asset_model,
    serial_no: groups[hid][0].asset_serial,
    date_of_supply: groups[hid][0].asset_installation_date,
    sl_no: "",
  }));
}

function renderPass(selection, bundle, map, covers) {
  const eng = new RegisterEngine(bundle.local_body_name);
  if (map) eng.map = map;
  const hardware = bundle.hardware || [];
  const complaintsByHw = groupBy(bundle.complaints, "hardware_id");
  const serviceByHw = groupBy(bundle.service, "hardware_id");
  const cmpAssets = [...hardware, ...pseudoAssetsFromOrphans(bundle.complaints, hardware)];
  const renderSection = (sec) => {
    if (sec === "hw_index") renderHwIndex(eng, hardware);
    else if (sec === "hw") renderHwRegister(eng, hardware, complaintsByHw, serviceByHw);
    else if (sec === "cmp_index") renderCmpIndex(eng, cmpAssets, complaintsByHw);
    else if (sec === "cmp_contacts") renderCmpContacts(eng, hardware, bundle.amc || []);
    else if (sec === "cmp") renderCmpRegister(eng, cmpAssets, complaintsByHw, bundle.complaints || []);
    else if (sec === "amc") renderAmc(eng, bundle.amc || []);
    else if (sec === "connectivity") renderConnectivity(eng, bundle.connectivity || []);
    else if (sec === "service") renderService(eng, bundle.service || []);
  };
  REGISTER_GROUPS
    .filter(g => g.sections.some(s => selection.includes(s)))
    .forEach(g => {
      const startPage = eng.page;
      if (covers) renderCover(eng, g, bundle);
      g.sections.forEach(sec => { if (selection.includes(sec)) renderSection(sec); });
      if (covers) renderClosing(eng, g, bundle);
      eng.map.groupPages = eng.map.groupPages || {};
      eng.map.groupPages[g.id] = eng.page - startPage;
    });
  return eng;
}

export function buildRegisterDocument(selection, bundle, opts = {}) {
  const covers = opts.covers !== false;
  // Pass 1 — measure real page numbers for every asset and register group
  const probe = renderPass(selection, bundle, null, covers);
  // Pass 2 — render with correct Index Page references + certificate page counts
  const eng = renderPass(selection, bundle, probe.map, covers);
  eng.drawFooters();
  return eng.doc;
}

export function sectionsForOption(optionId) {
  const opt = PRINT_OPTIONS.find(o => o.id === optionId);
  return opt ? opt.sections : PRINT_OPTIONS[5].sections;
}

export function filterBundleForAsset(bundle, hardwareId) {
  return {
    ...bundle,
    hardware: (bundle.hardware || []).filter(h => h.id === hardwareId),
    complaints: (bundle.complaints || []).filter(c => c.hardware_id === hardwareId),
    service: (bundle.service || []).filter(s => s.hardware_id === hardwareId),
    amc: (bundle.amc || []).filter(a => a.hardware_id === hardwareId),
  };
}

export function registerBlobUrl(selection, bundle, opts) {
  return buildRegisterDocument(selection, bundle, opts).output("bloburl");
}

export function downloadRegisterPDF(selection, bundle, filename = "Registers.pdf", opts) {
  buildRegisterDocument(selection, bundle, opts).save(filename);
}

export function printRegisterPDF(selection, bundle, opts) {
  const url = registerBlobUrl(selection, bundle, opts);
  const w = window.open(url, "_blank");
  if (w) {
    w.addEventListener("load", () => setTimeout(() => w.print(), 400));
  }
  return url;
}
